/**
 * locationCoords — resolve a base location's lat/long for the solar/FAA-night
 * indicators, and derive the ORGANIZATION's timezone from its locations.
 *
 * Promote-lifted from `calendar-day-v2/utils/locationCoords.js` (CalAudit-P0,
 * Task:4): Day, the Availability Finder, and now Month + Week all need
 * `fetchOrgLocationCoordinates()`'s org-timezone derivation, so this lives in
 * `calendar-common/` as the single shared implementation. `calendar-day-v2`
 * re-exports from here so its existing import keeps working unchanged.
 *
 * The calendar locations API carries NO coordinates today (id/name/IATA/
 * airportName/city/timezone only — see php/models/Calendar.php locations
 * query), so resolution is layered:
 *
 *   1. Coordinate-ish fields on the DTO, read defensively (`latitude`/
 *      `longitude`, `lat`/`lng`|`lon`) — future-proof for when the backend
 *      adds them to location setup (planned PR).
 *   2. A per-location USER OVERRIDE persisted in localStorage — the "set
 *      coordinates" prompt on bases with no known position (frontend-only
 *      stand-in for the missing setup field).
 *   3. A small built-in ICAO/IATA table (extend freely as orgs need).
 *
 * Returns `{ lat, lng }` (east-positive lng) or `null` when unknown.
 *
 * @module locationCoords
 */

import LocationsApi from '../../api/locationsApi.js';

const LS_KEY = 'dayv2.locationCoords'; // { [locationID]: {lat, lng} } — kept as-is; renaming would orphan existing overrides.

// Known airfields (ICAO and IATA keys, uppercase). Seeded with the design
// mock's Hawaiian bases; add entries as operators come aboard.
const AIRPORTS = {
	PHOG: { lat: 20.8986, lng: -156.4305 },
	OGG: { lat: 20.8986, lng: -156.4305 },
	PHKO: { lat: 19.7388, lng: -156.0456 },
	KOA: { lat: 19.7388, lng: -156.0456 },
	PHLI: { lat: 21.976, lng: -159.339 },
	LIH: { lat: 21.976, lng: -159.339 },
	PHTO: { lat: 19.7203, lng: -155.0485 }, // Hilo
	ITO: { lat: 19.7203, lng: -155.0485 },
	PHNL: { lat: 21.3187, lng: -157.9224 }, // Honolulu
	HNL: { lat: 21.3187, lng: -157.9224 }
};

function readOverrides() {
	try {
		return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}') || {};
	} catch (e) {
		return {};
	}
}

const num = v => {
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};

const valid = (lat, lng) =>
	lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

/**
 * Parse the location table's `coordinate` string. The field accepts two
 * formats (see `Locations.php:37` validation):
 *   decimal  — `20.8986, -156.4305`
 *   DMS      — `20° 53.92' 0", -156° 25.83' 0"` (minutes/seconds optional)
 *
 * @param {string} str
 * @returns {{lat:number, lng:number}|null}
 */
export function parseCoordinateString(str) {
	const s = String(str || '').trim();
	if (!s) return null;

	if (s.includes('°')) {
		const parts = s.split(',');
		if (parts.length < 2) return null;
		const dms = part => {
			const m = /^(-|\+)?\s*([0-9]{1,3})\s*°\s*([0-9.]*)'?\s*([0-9.]*)"?/.exec(part.trim());
			if (!m) return null;
			const v = Number(m[2]) + (m[3] ? Number(m[3]) / 60 : 0) + (m[4] ? Number(m[4]) / 3600 : 0);
			return m[1] === '-' ? -v : v;
		};
		const lat = dms(parts[0]);
		const lng = dms(parts[1]);
		return valid(lat, lng) ? { lat, lng } : null;
	}

	const parts = s.split(',').map(p => Number(p.trim()));
	return parts.length >= 2 && valid(num(parts[0]), num(parts[1]))
		? { lat: parts[0], lng: parts[1] }
		: null;
}

/**
 * Fetch ALL of the current org's locations — with their `coordinate` field —
 * in one call via the refactored SYMFONY locations endpoint
 * (`GET /api/v1/org/{org}/location`, the same API the Locations page uses;
 * verified live to include `coordinate` for every base). Returns a map keyed
 * by location id: `{ [id]: {lat,lng}|null }`.
 *
 * (Replaces a per-location legacy `getLocationByID` fetch — that endpoint
 * does return the field but with `success: null`, which the envelope check
 * rejected; the Locations module is Symfony-refactored, so this is also the
 * maintained path.)
 *
 * @returns {Promise<{coords: Object<string, {lat:number,lng:number}|null>, orgTimezone: string|null}>}
 */
export async function fetchOrgLocationCoordinates() {
	try {
		const response = await LocationsApi.getLocations();
		const payload = response && response.data !== undefined ? response.data : response;
		const list = Array.isArray(payload)
			? payload
			: payload && Array.isArray(payload.data)
				? payload.data
				: [];
		const coords = {};
		const tzCounts = {};
		list.forEach(l => {
			if (!l) return;
			const id = l.id ?? l.locationID;
			if (id == null) return;
			coords[String(id)] = parseCoordinateString(l.coordinate);
			// `timeZoneId` is already an Olson name (e.g. "America/New_York").
			const tz = l.timeZoneId || l.locationTimezoneId || null;
			if (tz) tzCounts[tz] = (tzCounts[tz] || 0) + 1;
		});
		// The ORGANIZATION's timezone, derived as the most common zone across its
		// locations (the org entity itself carries none via the API). Used as the
		// calendar's default display tz — the user's profile tz was wrong for
		// multi-org users (a Honolulu profile made Eastern orgs render in HST).
		let orgTimezone = null;
		let best = 0;
		Object.entries(tzCounts).forEach(([tz, n]) => {
			if (n > best) {
				best = n;
				orgTimezone = tz;
			}
		});
		return { coords, orgTimezone };
	} catch (e) {
		return { coords: {}, orgTimezone: null };
	}
}

/**
 * @param {Object} location  Bridge location object (`locationID`,
 *   `locationIATA`, plus whatever the DTO grows later).
 * @param {Object} [dbCoords] Map `{ [locationID]: {lat,lng}|null }` of
 *   coordinates fetched from the location table (see
 *   `fetchLocationCoordinate`) — the AUTHORITATIVE source when present.
 * @returns {{lat:number, lng:number}|null}
 */
export function resolveLocationCoords(location, dbCoords) {
	if (!location) return null;
	const id = String(location.locationID);

	// 1. Explicit DTO fields (future backend support).
	const dtoLat = num(location.latitude ?? location.lat);
	const dtoLng = num(location.longitude ?? location.lng ?? location.lon);
	if (valid(dtoLat, dtoLng)) return { lat: dtoLat, lng: dtoLng };

	// 2. The location table's `coordinate` field (fetched via legacy endpoint).
	const db = dbCoords && dbCoords[id];
	if (db && valid(num(db.lat), num(db.lng))) return { lat: Number(db.lat), lng: Number(db.lng) };

	// 3. Built-in airfield table by ICAO/IATA code.
	// `iata` FIRST, then `locationIATA`: the two DTOs that reach the calendar
	// spell this differently, and reading only `locationIATA` returned null for
	// every org whose payload uses `iata` — the same trap the Day page guards
	// against when it reads `l.iata || l.locationIATA` for display. Silent
	// before the weather strip; now it costs the whole nearby-field search,
	// because no coordinates means no bounding-box lookup.
	const code = String(location.iata || location.locationIATA || '')
		.trim()
		.toUpperCase();
	if (code && AIRPORTS[code]) return AIRPORTS[code];

	// 4. Legacy per-browser override (pre-DB-fetch releases) — lowest priority.
	const o = readOverrides()[id];
	if (o && valid(num(o.lat), num(o.lng))) return { lat: Number(o.lat), lng: Number(o.lng) };

	return null;
}

/**
 * Persist a user-entered coordinate override for a location.
 * @returns {boolean} true when stored.
 */
export function saveLocationCoords(locationID, lat, lng) {
	const la = num(lat);
	const ln = num(lng);
	if (!valid(la, ln)) return false;
	try {
		const all = readOverrides();
		all[String(locationID)] = { lat: la, lng: ln };
		window.localStorage.setItem(LS_KEY, JSON.stringify(all));
		return true;
	} catch (e) {
		return false;
	}
}

export default resolveLocationCoords;
